/* eslint-disable jsx-a11y/anchor-is-valid */
import MenuDetailModal from 'features/MenuDetail/pages/MenuDetail';
import { TargetMenu } from 'models';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, Form, ModalProps } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TfiReload } from 'react-icons/tfi';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

export interface SelectTargetMenuModalTableProps {
  data: TargetMenu[];
  onReload?: () => void;
  onReflect?: (menuIds: number[]) => void;
}

function SelectTargetMenuModalTable({ data, onReload, onReflect }: SelectTargetMenuModalTableProps) {
  const { t } = useTranslation();

  // states
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, forceRender] = useState(0);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuDetailModalData, setMenuDetailData] = useState<{ menuId: number }>();

  // refs
  const menuDetailModalRef = useRef<ModalProps>(MenuDetailModal);
  const selectedMenuIdsRef = useRef<number[]>([]);
  const btnAddRef = useRef<HTMLButtonElement>(null);

  // effects
  useEffect(() => {
    if (btnAddRef.current) btnAddRef.current.disabled = true;
  }, []);

  useEffect(() => {
    handleUnSelectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (showMenuModal && menuDetailModalData) {
      handleOpenMenuDetailModal(menuDetailModalData.menuId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenuModal]);

  // functions
  function updateStatusDisabledButtonAdd() {
    if (btnAddRef.current) {
      if (selectedMenuIdsRef.current.length > 0) {
        btnAddRef.current.disabled = false;
      } else {
        btnAddRef.current.disabled = true;
      }
    }
  }

  // events
  function onSelectedRowChanged(e: ChangeEvent<HTMLInputElement>) {
    const menuId = Number(e.target.value);
    if (e.target.checked) {
      if (!selectedMenuIdsRef.current.includes(menuId)) {
        selectedMenuIdsRef.current.push(menuId);
      }
    } else {
      if (selectedMenuIdsRef.current.includes(menuId)) {
        selectedMenuIdsRef.current = selectedMenuIdsRef.current.filter((x) => x !== menuId);
      }
    }
    updateStatusDisabledButtonAdd();
  }

  function handleSelectAll() {
    const newSelectedMenuIds = data.map((menu) => menu.id);
    selectedMenuIdsRef.current = newSelectedMenuIds;
    forceRender((prev) => prev + 1);
    updateStatusDisabledButtonAdd();
  }

  function handleUnSelectAll() {
    selectedMenuIdsRef.current = [];
    forceRender((prev) => prev + 1);
    updateStatusDisabledButtonAdd();
  }

  function handleRefresh() {
    onReload?.();
    handleUnSelectAll();
  }

  function handleReflect() {
    onReflect?.(selectedMenuIdsRef.current);
  }

  function handleOpenMenuModal(menuId: number) {
    setShowMenuModal(true);
    setMenuDetailData({ menuId: menuId });
  }

  function handleOpenMenuDetailModal(menuId: number) {
    handleOpenPopupSelectedMenuId(menuId);
  }

  function handleMenuDetailModalClose() {
    menuDetailModalRef.current.onReset();
    setShowMenuModal(false);
    setMenuDetailData(undefined);
  }

  function handleOpenPopupSelectedMenuId(menuId: number) {
    const dataDate = new Date(); // today

    const strDataDate = dataDate.getFullYear() + '' + (dataDate.getMonth() + 1) + '' + dataDate.getDate();
    menuDetailModalRef.current.openModalExternal(menuId, strDataDate);
  }

  return (
    <>
      <div className="d-flex justify-content-between mb-2">
        <div className="d-flex align-items-center">
          <div className="d-inline-block me-4">
            <Button
              variant="link"
              className="px-0 text-decoration-none"
              onClick={handleSelectAll}
              style={{ minWidth: 'auto' }}
            >
              {t('SelectTargetMenuModal.select_all')}
            </Button>
            <span>／</span>
            <Button
              variant="link"
              className="px-0 text-decoration-none"
              onClick={handleUnSelectAll}
              style={{ minWidth: 'auto' }}
            >
              {t('SelectTargetMenuModal.unselect_all')}
            </Button>
          </div>
          <div className="d-inline-block">
            <Button
              variant="link"
              className="d-flex align-items-center px-0 text-decoration-none"
              onClick={handleRefresh}
            >
              <div
                className="d-inline-flex align-items-center justify-content-center me-1 bg-primary rounded-circle"
                style={{ width: 18, height: 18 }}
              >
                <TfiReload color="#fff" />
              </div>
              <span>{t('SelectTargetMenuModal.reload')}</span>
            </Button>
          </div>
        </div>
        <div>
          <Button ref={btnAddRef} className="btn--dark-green" onClick={handleReflect}>
            {t('SelectTargetMenuModal.add')}
          </Button>
        </div>
      </div>
      <div className="d-flex flex-column" style={{ height: '352px', backgroundColor: '#999' }}>
        <div className="dan-table">
          <div className="dan-thead">
            <div className="dan-tr">
              <div className="dan-th" style={{ width: '10%' }}>
                {t('SelectTargetMenuModal.select')}
              </div>
              <div className="dan-th border-start-0" style={{ width: '30%' }}>
                {t('SelectTargetMenuModal.menu_code')}
              </div>
              <div className="dan-th border-start-0" style={{ width: '60%' }}>
                {t('SelectTargetMenuModal.menu_name')}
              </div>
            </div>
          </div>
        </div>
        <div className="flex-grow-1 flex-shrink-1">
          <AutoSizer>
            {({ height, width }) => (
              <FixedSizeList
                className="fixed-size-list"
                width={width}
                height={height}
                itemSize={32}
                itemCount={data.length}
                itemData={data}
              >
                {({ style, index }) => {
                  const menu = data[index];

                  return (
                    <div key={index} id={`row-${index}`} className={`dan-tr d-flex`} style={style}>
                      <div className="dan-td d-flex justify-content-center" style={{ width: '10%' }}>
                        <Form.Check
                          type="checkbox"
                          className="custom-checkbox"
                          value={menu.id}
                          onChange={onSelectedRowChanged}
                          defaultChecked={selectedMenuIdsRef.current.includes(menu.id)}
                          // disabled={except_menu_ids?.includes(menu.id)}
                        />
                      </div>
                      <div className="dan-td d-flex justify-content-center" style={{ width: '30%' }}>
                        {menu.cd}
                      </div>
                      <div className="dan-td d-flex justify-content-center" style={{ width: '60%' }}>
                        <a
                          className="px-0 btn btn-link"
                          style={{ minWidth: 'inherit' }}
                          rel="noreferrer"
                          tabIndex={-1}
                          onClick={handleOpenMenuModal.bind(null, menu.id)}
                        >
                          {menu.name}
                        </a>
                      </div>
                    </div>
                  );
                }}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      </div>

      {showMenuModal &&
        createPortal(
          <MenuDetailModal
            ref={menuDetailModalRef}
            onMenuModalClose={handleMenuDetailModalClose}
            // menuCallBack={handleMenuDetailModalSaveSuccess}
          />,
          document.body
        )}
    </>
  );
}

export default SelectTargetMenuModalTable;
